import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, RefreshCw, AlertCircle } from 'lucide-react';
import { LoadingSpinner } from './ui';
import { logger } from '../utils/logger';

interface NavigationFeedbackProps {
  className?: string;
}

export const NavigationFeedback: React.FC<NavigationFeedbackProps> = ({ className = '' }) => {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);

  // Reset states on location change
  useEffect(() => {
    setIsNavigating(false);
    setNavigationError(null);
  }, [location.pathname]);

  const handleNavigation = async (path: string, labelKey: string) => {
    try {
      setIsNavigating(true);
      setNavigationError(null);
      
      // Add small delay for better UX feedback
      await new Promise(resolve => setTimeout(resolve, 200));
      
      navigate(path);
    } catch (error) {
      setNavigationError(t('feedback.error', { target: t(labelKey) }));
      logger.error('Navigation error:', error);
    } finally {
      setIsNavigating(false);
    }
  };

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleNavigation('/', 'navigation.feedback.home');
    }
  };

  const goHome = () => {
    handleNavigation('/', 'navigation.feedback.home');
  };

  const refreshPage = () => {
    window.location.reload();
  };

  if (navigationError) {
    return (
      <div className={`flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <AlertCircle size={16} className="text-red-600" />
        <span className="text-sm text-red-700">{navigationError}</span>
        <button
          onClick={() => setNavigationError(null)}
          className="ml-auto text-red-600 hover:text-red-800 text-sm underline"
        >
          {t('feedback.close')}
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {isNavigating && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-xl backdrop-blur-sm animate-pulse">
          <LoadingSpinner size="sm" className="text-blue-600" />
          <span className="text-[10px] font-bold tracking-widest text-blue-700 uppercase">{t('feedback.navigating')}</span>
        </div>
      )}
      
      <button
        onClick={goBack}
        disabled={isNavigating}
        className="flex items-center gap-2 px-4 py-1.5 text-slate-500 hover:text-slate-900 bg-white/50 hover:bg-white border border-slate-200/50 hover:border-slate-300 rounded-xl transition-all duration-200 disabled:opacity-50 active:scale-95 group"
        title={t('feedback.back')}
      >
        <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
        <span className="text-xs font-bold uppercase tracking-tight">{t('feedback.back')}</span>
      </button>

      <button
        onClick={goHome}
        disabled={isNavigating}
        className="flex items-center gap-2 px-4 py-1.5 text-slate-500 hover:text-indigo-600 bg-white/50 hover:bg-indigo-50 border border-slate-200/50 hover:border-indigo-200 rounded-xl transition-all duration-200 disabled:opacity-50 active:scale-95 group"
        title={t('feedback.home')}
      >
        <Home size={16} className="group-hover:scale-110 transition-transform" />
        <span className="text-xs font-bold uppercase tracking-tight">{t('feedback.home')}</span>
      </button>

      <button
        onClick={refreshPage}
        disabled={isNavigating}
        className="flex items-center gap-2 px-4 py-1.5 text-slate-500 hover:text-amber-600 bg-white/50 hover:bg-amber-50 border border-slate-200/50 hover:border-amber-200 rounded-xl transition-all duration-200 disabled:opacity-50 active:scale-95 group"
        title={t('feedback.refresh')}
      >
        <RefreshCw size={16} className="group-hover:rotate-180 transition-transform duration-500" />
        <span className="text-xs font-bold uppercase tracking-tight">{t('feedback.refresh')}</span>
      </button>
    </div>
  );
};

export default NavigationFeedback;
